"use client"

import { usePathname } from "next/navigation"
import { useUserRole } from "@/providers/user-role-provider"
import { NotificationsPanel } from "@/components/notifications-panel"
import { BarberNotificationsPanel } from "@/components/barber-notifications-panel"
import { ErrorBoundary } from "@/components/error-boundary"
import { isForceDarkBooking, stripLocalePrefix, isGlassNavPath } from "@/i18n/routing"

// Desktop counterpart to MobileNotificationsHeader. Floats the bell
// top-right on every authenticated tablet/desktop page. It's the right-hand
// end of a three-part chrome: wordmark top-centre, bell top-right, nav
// launcher bottom-centre.
//
// Visibility rules:
//   - Authenticated only.
//   - Hidden on /auth/* and /clients/* (focused-form chrome).
//   - Hidden on routes that render their own header (/, /changelog, /traction).
//   - md+ only — mobile bell lives in MobileNotificationsHeader.

// Kept in step with BrandHeader's hide list: wherever the wordmark shows, the
// bell shows, so the top strip is never half-present.
const HIDE_PREFIXES: ReadonlyArray<string> = ["/auth", "/clients"]

function shouldHide(rawPathname: string | null): boolean {
  if (!rawPathname) return false
  // Locale-agnostic: the Spanish landing at /es must hide exactly like /.
  const pathname = stripLocalePrefix(rawPathname)
  // Pages the glass nav owns carry the whole site in their menu panel — this
  // would be a second, competing one on the same screen.
  if (isGlassNavPath(pathname)) return true
  return HIDE_PREFIXES.some(p => pathname === p || pathname.startsWith(`${p}/`))
}

export function DesktopNotificationsHeader() {
  const pathname = usePathname()
  const { userId, role, actualUserType, isLoading } = useUserRole()

  if (isLoading) return null
  if (!userId) return null
  if (shouldHide(pathname)) return null

  // Active mode wins over the stored role. A dual-role user (an owner who
  // also cuts) standing in Barber mode was getting the OWNER panel, because
  // their DB role is 'owner' — so an appointment notification routed them to
  // the owner dashboard instead of their bookings.
  const effective = role ?? actualUserType
  const isBarber = effective === "barber"

  // On the force-dark booking storefront the page is dark for everyone, so force
  // the bell icon white even when the device/app theme is light (its default
  // text-foreground would otherwise be dark = invisible on the dark page).
  const tint = isForceDarkBooking(pathname) ? "[&_button]:!text-white" : ""

  return (
    <div className={`hidden md:block fixed safe-top-bell-md right-3 lg:right-[1.125rem] z-40 ${tint}`}>
      <ErrorBoundary>
        {isBarber ? <BarberNotificationsPanel /> : <NotificationsPanel />}
      </ErrorBoundary>
    </div>
  )
}
